import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PaymentMethods } from '@cargos/sprintpay-models';
import { combineLatest, distinctUntilChanged, map, Observable, of, switchMap, take } from 'rxjs';
import { ConfigService } from 'src/app/shared/services';
import { PaymentMethodComplete } from '../../../shared/models/payment-method.model';
import { PaymentMethodsHandlerService } from '../../../shared/services/payment-methods-handler.service';
import { CreditCardVerificationDialogComponent } from '../credit-card-verification-dialog.component';
import {
    CreditCardVerificationData,
    CreditCardVerificationDialogResult,
} from '../models/credit-card-verification.models';
import { PaymentMethodItemWithBillingAddress } from '../types';

/**
 * @description Service for handling credit card verification before payment processing.
 * Checks if credit card verification is enabled and if the selected payment method
 * requires billing address verification.
 */
@Injectable({
    providedIn: 'root',
})
export class CreditCardVerificationService {
    constructor(
        private readonly matDialog: MatDialog,
        private readonly configService: ConfigService,
        private readonly paymentMethodsHandlerService: PaymentMethodsHandlerService
    ) {}

    /**
     * @description Checks if credit card verification is required and handles the verification process.
     * @returns Observable<boolean> - Returns true if verification passed or not required, false if verification failed or was cancelled.
     */
    public verifyCreditCardIfNeeded$(): Observable<boolean> {
        if (!this.configService.isCreditCardVerificationEnabled()) {
            return of(true);
        }

        return this.getUnverifiedCreditCardData$().pipe(
            switchMap((unverifiedCardData: CreditCardVerificationData | null) => {
                if (!unverifiedCardData) {
                    return of(true);
                }

                return this.openVerificationDialog(unverifiedCardData);
            }),
            distinctUntilChanged()
        );
    }

    /**
     * @description Gets unverified credit card data from both threshold payment methods.
     * Checks below and above threshold payment methods for unverified credit cards.
     * @returns Observable<CreditCardVerificationData | null>
     */
    private getUnverifiedCreditCardData$(): Observable<CreditCardVerificationData | null> {
        return combineLatest([
            this.paymentMethodsHandlerService.getPaymentMethodSelectedComplete$(false),
            this.paymentMethodsHandlerService.getPaymentMethodSelectedComplete$(true),
        ]).pipe(
            take(1),
            map(
                ([belowThreshold, aboveThreshold]: [
                    PaymentMethodComplete | null,
                    PaymentMethodComplete | null,
                ]): CreditCardVerificationData | null => {
                    const unverifiedCreditCard = this.findUnverifiedCreditCard([belowThreshold, aboveThreshold]);

                    if (!unverifiedCreditCard) {
                        return null;
                    }

                    return this.extractCreditCardVerificationData(unverifiedCreditCard);
                }
            )
        );
    }

    /**
     * @description Finds the first unverified credit card from an array of payment methods.
     * @param paymentMethods Array of PaymentMethodComplete to check
     * @returns PaymentMethodComplete | null
     */
    private findUnverifiedCreditCard(paymentMethods: (PaymentMethodComplete | null)[]): PaymentMethodComplete | null {
        return (
            paymentMethods.find((paymentMethod): paymentMethod is PaymentMethodComplete => {
                return this.isCreditCardRequiringVerification(paymentMethod);
            }) || null
        );
    }

    /**
     * @description Checks if a payment method is a credit card that requires verification.
     * @param paymentMethod PaymentMethodComplete to check
     * @returns boolean
     */
    private isCreditCardRequiringVerification(paymentMethod: PaymentMethodComplete | null): boolean {
        if (!paymentMethod?.paymentMethodComplete) {
            return false;
        }

        if (paymentMethod.name !== PaymentMethods.CREDIT_CARD) {
            return false;
        }

        const paymentMethodItem = paymentMethod.paymentMethodComplete as PaymentMethodItemWithBillingAddress;
        const accountingDetails = paymentMethodItem?.accountingDetails;

        return accountingDetails?.isVerified === false;
    }

    /**
     * @description Extracts credit card verification data from a PaymentMethodComplete object.
     * @param paymentMethod PaymentMethodComplete containing credit card data
     * @returns CreditCardVerificationData
     */
    private extractCreditCardVerificationData(paymentMethod: PaymentMethodComplete): CreditCardVerificationData {
        const paymentMethodItem = paymentMethod.paymentMethodComplete as PaymentMethodItemWithBillingAddress;
        const billingAddress = paymentMethodItem?.billingAddress;
        const accountingDetails = paymentMethodItem?.accountingDetails;

        return {
            token: billingAddress?.token ?? paymentMethod.token ?? '',
            lastFourDigits: accountingDetails?.lastFourDigits ?? billingAddress?.cardLastFour,
            cardHolderName: billingAddress?.cardHolderName,
            cardType: accountingDetails?.cardType ?? billingAddress?.variant,
            billingAddress: {
                street: billingAddress?.street ?? '',
                houseNumberOrName: billingAddress?.houseNumberOrName ?? '',
                city: billingAddress?.city ?? '',
                postalCode: billingAddress?.postalCode ?? '',
                country: billingAddress?.country ?? '',
            },
        };
    }

    /**
     * @description Opens the credit card verification dialog.
     * @param creditCardData CreditCardVerificationData to pass to the dialog
     * @returns Observable<boolean> - Returns true if verified, false otherwise
     */
    private openVerificationDialog(creditCardData: CreditCardVerificationData): Observable<boolean> {
        const dialogRef: MatDialogRef<CreditCardVerificationDialogComponent, CreditCardVerificationDialogResult> =
            this.matDialog.open(CreditCardVerificationDialogComponent, {
                data: { creditCardData },
                disableClose: true,
                panelClass: 'credit-card-verification-dialog-panel',
                width: '500px',
            });

        return dialogRef.afterClosed().pipe(
            take(1),
            map((result: CreditCardVerificationDialogResult | undefined) => {
                return result?.verified === true;
            })
        );
    }
}
